import { KIO, OutputMode, Record, VirtualFE87V2_MuYan } from '@hid/define/define'
import defineType from '@hid/define/define_type'
import { DVKFromVK, HidFromKK, HIDFromVk } from '@hid/define/define_fn'
import { splitIntoBytes } from '../tool'
import { encodeCombinationKeys, IsModifierKey } from '@hid/define/define_hid'
import { DevicePid } from '@/define/define'
import { defaultVK } from '@hid/define/define_keys'

export enum zhihuiwuxianCmd {
  DeviceInfo = 0x12,
  setKey = 0x18,
  setMacro = 0x1c,
  getGeneralInfo = 0x14, // 通用信息
  setGeneralInfo = 0x15,
  resetDevice = 0x20,
}

export enum zhihuiwuxianKeyType {
  normal = 0x10,
  media = 0x20,
  system = 0x30,
  mouse = 0x40,
  macro = 0x50,
  macro_times = 0x51,
  combination = 0x70, // 组合键
}

export enum zhihuiwuxianMacroKeyType {
  normal = 0x01,
  hotCode = 0x02,
}

export const fe87v2MuYanMap = new Map<number, number>([
  // --- 第一行 (Function Keys & System) ---
  [0, 0x29], // Esc
  [1, 0x3a], // F1
  [2, 0x3b], // F2
  [3, 0x3c], // F3
  [4, 0x3d], // F4
  [5, 0x3e], // F5
  [6, 0x3f], // F6
  [7, 0x40], // F7
  [8, 0x41], // F8
  [9, 0x42], // F9
  [10, 0x43], // F10
  [11, 0x44], // F11
  [12, 0x45], // F12
  [13, 0x46], // Print Screen
  [14, 0x47], // Scroll Lock
  [15, 0x48], // Pause

  // --- 第二行 (Number Row) ---
  [16, 0x35], // ` ~
  [17, 0x1e], // 1
  [18, 0x1f], // 2
  [19, 0x20], // 3
  [20, 0x21], // 4
  [21, 0x22], // 5
  [22, 0x23], // 6
  [23, 0x24], // 7
  [24, 0x25], // 8
  [25, 0x26], // 9
  [26, 0x27], // 0
  [27, 0x2d], // - _
  [28, 0x2e], // = +
  [29, 0x2a], // Backspace
  [30, 0x49], // Insert
  [31, 0x4a], // Home
  [47, 0x4b], // PageUp

  // --- 第三行 (QWERTY Row) ---
  [32, 0x2b], // Tab
  [33, 0x14], // Q
  [34, 0x1a], // W
  [35, 0x08], // E
  [36, 0x15], // R
  [37, 0x17], // T
  [38, 0x1c], // Y
  [39, 0x18], // U
  [40, 0x0c], // I
  [41, 0x12], // O
  [42, 0x13], // P
  [43, 0x2f], // [ {
  [44, 0x30], // ] }
  [45, 0x31], // \ |
  [46, 0x4c], // Delete
  [62, 0x4d], // End
  [63, 0x4e], // PageDown

  // --- 第四行 (ASDF Row) ---
  [48, 0x39], // CapsLock
  [49, 0x04], // A
  [50, 0x16], // S
  [51, 0x07], // D
  [52, 0x09], // F
  [53, 0x0a], // G
  [54, 0x0b], // H
  [55, 0x0d], // J
  [56, 0x0e], // K
  [57, 0x0f], // L
  [58, 0x33], // ; :
  [59, 0x34], // ' "
  [61, 0x28], // Enter

  // --- 第五行 (ZXCV Row) ---
  [64, 0xe1], // ShiftLeft
  [66, 0x1d], // Z
  [67, 0x1b], // X
  [68, 0x06], // C
  [69, 0x19], // V
  [70, 0x05], // B
  [71, 0x11], // N
  [72, 0x10], // M
  [73, 0x36], // , <
  [74, 0x37], // . >
  [75, 0x38], // / ?
  [77, 0xe5], // ShiftRight
  [78, 0x52], // ArrowUp

  // --- 第六行 (Bottom Row) ---
  [80, 0xe0], // ControlLeft
  [81, 0xe3], // WinLeft (MetaLeft)
  [82, 0xe2], // AltLeft
  [85, 0x2c], // Space
  [89, 0xe6], // AltRight
  [90, 0xff], // FN (WakeUp)
  [91, 0x65], // App (Menu键)
  [92, 0xe4], // ControlRight
  [93, 0x50], // ArrowLeft
  [94, 0x51], // ArrowDown
  [95, 0x4f], // ArrowRight
])
function reverseMap<K, V>(map: Map<K, V>): Map<V, K> {
  const reversed = new Map<V, K>()
  for (const [key, value] of map) {
    reversed.set(value, key)
  }
  return reversed
}
export const fe87v2MuYanReverseMap = reverseMap(fe87v2MuYanMap)
export const zhihuiwuxianSetKeyData = (params: KIO): [number, number, number] => {
  let type = zhihuiwuxianKeyType.normal

  if (params.Output.Mode == OutputMode.Key && params.Output.VK == defaultVK) {
    const hid = HidFromKK(params.Input.KK, VirtualFE87V2_MuYan)
    return [zhihuiwuxianKeyType.normal, 0, hid]
  }

  if (params.Output.Mode === OutputMode.Macro) {
    type = zhihuiwuxianKeyType.macro
    if (params.Output.Extension[0] == 1) {
      type = zhihuiwuxianKeyType.macro_times
    }
    if (type == zhihuiwuxianKeyType.macro_times) {
      return [type, params.Output.DataIndex, params.Output.Extension[1]]
    } else {
      return [type, params.Output.DataIndex, params.Output.Extension[0]]
    }
  }

  const hid = HIDFromVk(params.Output.VK)
  if (params.Input.WithModifier.length > 0) {
    if (hid == undefined) {
      throw new Error('Not Found Key : ' + params.Output.VK)
    }
    type = zhihuiwuxianKeyType.combination
    return [zhihuiwuxianKeyType.combination, encodeCombinationKeys(params.Input.WithModifier), hid]
  }

  if (defineType.isMedia(params.Output.VK)) {
    const dvk = DVKFromVK(params.Output.VK, VirtualFE87V2_MuYan)
    const [consumerLowByte, consumerHighByte] = splitIntoBytes(dvk)
    return [zhihuiwuxianKeyType.media, consumerLowByte, consumerHighByte]
  }

  if (defineType.isMouse(params.Output.VK)) {
    const dvk = DVKFromVK(params.Output.VK, VirtualFE87V2_MuYan)
    const [consumerLowByte, consumerHighByte] = splitIntoBytes(dvk)
    return [zhihuiwuxianKeyType.mouse, consumerLowByte, consumerHighByte]
  }

  if (hid == undefined) {
    throw new Error('Not Found Key : ' + params.Output.VK)
  }
  return [type, 0, hid]
}

export const zhihuiwuxianMakeMarcoData = (macro: Record[]): number[] => {
  const macroData = [macro.length & 0xff, macro.length >> 8, 0, 0]
  macro.forEach((item) => {
    let keyType = 0

    if (item.Press) {
      keyType = 0x80
    }

    let hid = 0
    if (IsModifierKey(item.Key)) {
      keyType = keyType | zhihuiwuxianMacroKeyType.hotCode
      hid = encodeCombinationKeys([item.Key])
    } else {
      keyType = keyType | zhihuiwuxianMacroKeyType.normal
      const resHid = HIDFromVk(item.Key)
      if (resHid) {
        hid = resHid
      }
    }

    macroData.push(item.Delay & 0xff)
    macroData.push(item.Delay >> 8)
    macroData.push(hid)
    macroData.push(keyType)
  })
  return macroData
}

export const zhihuiwuxianKeysSet = (deviceID: DevicePid) => {
  const setList: number[][] = []

  for (let index = 0; index < 128; index++) {
    setList.push([zhihuiwuxianKeyType.normal, 0, 0])
  }

  if (deviceID == DevicePid.IROKFE87V2_MuYan) {
    for (const [key, hid] of fe87v2MuYanMap) {
      setList[key] = [zhihuiwuxianKeyType.normal, 0, hid]
    }
  }
  return setList
}

export const PollingRateMap = new Map<number, number>([
  [0, 8000],
  [1, 4000],
  [2, 2000],
  [3, 1000],
])
